import type { Dispatch } from "redux";
import { setCode, setFailed, setSaving } from "../redux/actions/UscSaveActions";
import Debug from "../../../common-deprecated/Debug";
import type { CCSaveCommonParametersType, FinanceOptionType } from "../../../common-deprecated/types/CommonTypes";
import { getLocationId } from "./externalPriceUtils";
import {
    type CarDeliveryDestination,
    type UscCarDeliveryConfig,
    CarDeliveryTransportationOption,
} from "./carDeliveryUtils";
import { type UscCheckoutType, UscContext } from "../../shared-logic/types/UscCommonTypes";
import type { UscActionsType, UscStateType } from "../redux/store";
import { getUscAccessoryPackParams } from "./accessoryUtils";

type ParametersType = CCSaveCommonParametersType & {
    TmefEnabled: boolean; // This one is also not relevant for but is required for other parties to know when to use TMEF.
    PurchaseType: UscCheckoutType | null;
    // DealerPostalCode is not relevant for the USC component so we never parse it at our side.
    // We already have more accurate dealer address info in A2D data.
    DealerPostalCode: string;
    CarDelivery?: {
        DeliveryDestination: "home" | "dealer";
        DeliveryType: "truck" | "driver";
        DeliveryAddress: {
            Address: string;
            City: string;
            Zip: string;
            Geo: {
                Latitude: number;
                Longitude: number;
            };
        };
        DeliveryDealerId: string | null;
        SourceDealerId: string;
        Price: { ExclVat: number; InclVat: number };
        DealerName?: string;
    };
    Accessories: string[];
    Packs: string[];
    LocalConfigurationId: string;
    NmscCode?: string;
};

/**
 * Return type of save service when we load / save
 * See https://cde.toyota-europe.com/confluence/display/UCP/CCSave+for+Used+Cars for more info
 */
export type UscTyCodeLoadType = {
    Brand: string; // capitalization is not guaranteed
    Country: string;
    Language: string;
    ID: string;
    LicensePlate: string;
    RetailerID: string;
    Status: "Available" | "Reserved" | "Sold";
    Type: "used-car" | "stock-car";
    VIN: string;
    Personal: {
        Code: string;
        ID: number;
        Link: string;
        Parameters: ParametersType & {
            "co-la": string; // countryCode-languageCode
        };
    };
};

/**
 * Object structure for requesting a tyCode from the save service
 */
export type UscTyCodeSaveType = {
    Country: string;
    Language: string;
    UsedCarID?: string;
    StockCarID?: string;
    Parameters: ParametersType;
};

/**
 * Internal version of UscTyCodeLoadType / UscTyCodeSaveType, used for loading / saving and mapped before sending
 */
export type UscLoadSaveCodeType = {
    id?: number;
    code?: string;
    financeOption: FinanceOptionType;
    vehicleForSaleId: string;
    financeQuoteId: string;
    insuranceQuoteId: string;
    userDepartmentCode: string;
    carDelivery: UscCarDeliveryConfig | null;
    purchaseType: UscCheckoutType | null;
    accessories: string[];
    packs: string[];
    // services: string[]; TODO: Implement services
};

/**
 * Formats the loaded CarDelivery object to the format expected by us
 */
export const formatCarDelivery = (
    carDelivery: UscTyCodeLoadType["Personal"]["Parameters"]["CarDelivery"],
): UscCarDeliveryConfig | null => {
    if (!carDelivery) return null;

    return {
        destination: carDelivery.DeliveryDestination as CarDeliveryDestination,
        transportationType: carDelivery.DeliveryType as CarDeliveryTransportationOption,
        address: {
            address: carDelivery.DeliveryAddress.Address,
            city: carDelivery.DeliveryAddress.City,
            zip: carDelivery.DeliveryAddress.Zip,
            geo: {
                lat: carDelivery.DeliveryAddress.Geo.Latitude,
                lon: carDelivery.DeliveryAddress.Geo.Longitude,
            },
        },
        dealerId: carDelivery.SourceDealerId,
        price: {
            cashPriceInclVat: carDelivery.Price.InclVat,
            cashPriceExclVat: carDelivery.Price.ExclVat,
        },
        dealerName: carDelivery.DealerName || "",
    };
};

/**
 * Formats the data we receive from the save service when saving / loading
 * We use the result to determine if the cached code is still up to date
 */
export const convertTyCodeLoadData = (response: UscTyCodeLoadType): UscLoadSaveCodeType => ({
    id: response.Personal.ID,
    code: response.Personal.Code,
    vehicleForSaleId: response.ID,
    financeQuoteId: response.Personal.Parameters.FinanceQuoteID || "",
    insuranceQuoteId: response.Personal.Parameters.InsuranceQuoteID || "",
    financeOption: response.Personal.Parameters.FinanceOption || "cash",
    userDepartmentCode: response.Personal.Parameters.UserDepartmentCode || "",
    carDelivery: formatCarDelivery(response.Personal.Parameters.CarDelivery),
    purchaseType: response.Personal.Parameters.PurchaseType,
    accessories: response.Personal.Parameters.Accessories,
    packs: response.Personal.Parameters.Packs,
});

export const ccSavePayloadSelector = (state: UscStateType): UscTyCodeSaveType => {
    const { commonSettings, finance, insurance, dealer, externalPrice, basket, uscSettings, carDelivery } = state;
    const { result } = state.vehicle || {};

    const saveObject: UscTyCodeSaveType = {
        Country: commonSettings.country,
        Language: commonSettings.language,
        Parameters: {
            FinanceQuoteID: finance.financeQuoteId || "",
            InsuranceQuoteID: insurance.insuranceQuoteId || "",
            UserDepartmentCode: getLocationId(dealer.dealer, externalPrice.userTaxLocationId) || "",
            DealerPostalCode: dealer.dealer?.address.zip || "",
            FinanceOption: basket.activeFinanceOption,
            TmefEnabled: uscSettings.enableExternalPrice,
            PurchaseType: basket.checkoutType,
            Accessories: basket.configuration.accessories.map((accessory) => accessory.id),
            Packs: basket.configuration.packs.map((pack) => pack.id),
            LocalConfigurationId: result
                ? getUscAccessoryPackParams(result, uscSettings.uscEnv).localConfigurationId
                : "",
            NmscCode: commonSettings.nmscCode || "",
        },
    };

    // CCSave uses the more 'readable' properties vs the ones used in the enum which are used for driiveme.
    const deliveryTypeMap = {
        [CarDeliveryTransportationOption.Truck]: "truck",
        [CarDeliveryTransportationOption.ProfessionalDriver]: "driver",
    } as const;

    if (carDelivery.deliveryConfig) {
        const { deliveryConfig } = carDelivery;
        saveObject.Parameters.CarDelivery = {
            DeliveryAddress: {
                Address: deliveryConfig.address?.address || "",
                City: deliveryConfig.address?.city || "",
                Zip: deliveryConfig.address?.zip || "",
                Geo: {
                    Latitude: deliveryConfig.address?.geo?.lat || 0,
                    Longitude: deliveryConfig.address?.geo?.lon || 0,
                },
            },
            DeliveryDealerId: deliveryConfig.dealerId || "",
            SourceDealerId: dealer.dealer?.id || "",
            DeliveryDestination: carDelivery.deliveryDestination,
            DeliveryType: deliveryTypeMap[carDelivery.deliveryTransportation],
            Price: {
                ExclVat: deliveryConfig.price?.cashPriceExclVat || 0,
                InclVat: deliveryConfig.price?.cashPriceInclVat || 0,
            },
            DealerName: deliveryConfig.dealerName || "",
        };
    }

    return saveObject;
};

/**
 * Saves the toyota code and dispatches the needed data to the redux store.
 * This function should not be awaited as it will only dispatch actions for the save reducer!
 */
export const saveTyCode = async (
    getState: () => UscStateType,
    dispatch: Dispatch<UscActionsType>,
    onFail?: (error: string) => void,
): Promise<void> => {
    const { vehicle, save, uscSettings } = getState();

    // don't allow saving when lazy loading vehicle data
    if (!vehicle.result) return;

    // Return when code is already saved or is being saved
    if (save.data || save.saving) return;

    try {
        dispatch(setSaving());

        // Object that will be saved to service
        const saveObject = ccSavePayloadSelector(getState());

        if (vehicle.type === UscContext.Used) saveObject.UsedCarID = vehicle.result.id;
        else saveObject.StockCarID = vehicle.result.id;

        const response = await fetch(`${uscSettings.loadSaveGatewayUrl as string}/config/find/json`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(saveObject),
            mode: "cors",
        });
        const responseJson: UscTyCodeLoadType = await response.json();

        // Get code value
        const data = convertTyCodeLoadData(responseJson);
        if (!data.code) throw new Error("Missing tyCode");

        dispatch(setCode(data));
    } catch (exception) {
        Debug.error("Failed to save and retrieve tyCode", exception);
        if (onFail) onFail(exception as string);
        dispatch(setFailed());
    }
};
